export const MANUFACTURER_PAYABLE_CATEGORIES = ["steel", "shed", "other"] as const;
export type ManufacturerPayableCategory = (typeof MANUFACTURER_PAYABLE_CATEGORIES)[number];

export const MANUFACTURER_PAYABLE_STATUSES = ["open", "due", "paid", "void"] as const;
export type ManufacturerPayableStatus = (typeof MANUFACTURER_PAYABLE_STATUSES)[number];

/** Org-typed manufacturer names already on the AP scroll — never a hardcoded vendor list. */
export function manufacturerNameSuggestions(names: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of names) {
    const n = raw.trim();
    if (!n) continue;
    const key = n.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(n);
  }
  return out;
}

export function roundUsd(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

/**
 * Dealer keeps the allowed deposit. Anything collected above that is manufacturer AP
 * (full-balance-up-front and over-collected deposits).
 */
export function manufacturerOverageUsd(collectedUsd: number, allowedDepositUsd: number): number {
  return Math.max(0, roundUsd(roundUsd(collectedUsd) - roundUsd(allowedDepositUsd)));
}

export function manufacturerPayableRemainingUsd(owedUsd: number, paidUsd: number): number {
  return Math.max(0, roundUsd(roundUsd(owedUsd) - roundUsd(paidUsd)));
}

/** Add a payment against remaining AP. Remaining floors at 0 if overpaid. */
export function applyManufacturerPaymentUsd(
  owedUsd: number,
  alreadyPaidUsd: number,
  paymentUsd: number
): { amount_paid_usd: number; remaining_usd: number; paid_in_full: boolean } {
  const amount_paid_usd = roundUsd(roundUsd(alreadyPaidUsd) + roundUsd(paymentUsd));
  const remaining_usd = manufacturerPayableRemainingUsd(owedUsd, amount_paid_usd);
  return { amount_paid_usd, remaining_usd, paid_in_full: remaining_usd <= 0 };
}

/**
 * Collected $ for manufacturer AP. Prefer captured host `initial_payment`
 * (what the customer paid); fall back to order `total` when that is the
 * full-balance-up-front case and initial payment is missing.
 */
export function payablesCollectedUsdFromHostMoney(args: {
  initial_payment?: number | null;
  total?: number | null;
}): number | null {
  const deposit = args.initial_payment;
  if (typeof deposit === "number" && Number.isFinite(deposit) && deposit > 0) {
    return roundUsd(deposit);
  }
  const total = args.total;
  if (typeof total === "number" && Number.isFinite(total) && total > 0) {
    return roundUsd(total);
  }
  return null;
}

export function payablesCollectedUsdFromDealMetadata(metadata: unknown): number | null {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return null;
  const snap = (metadata as { inventory_host_accounting_snapshot?: unknown })
    .inventory_host_accounting_snapshot;
  if (!snap || typeof snap !== "object" || Array.isArray(snap)) return null;
  const o = snap as Record<string, unknown>;
  const num = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v) ? v : null;
  return payablesCollectedUsdFromHostMoney({
    initial_payment: num(o.initial_payment),
    total: num(o.total),
  });
}

export function salePathFromDealMetadata(metadata: unknown): "cash" | "rto" | "finance" | null {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return null;
  const v = (metadata as { sale_path?: unknown }).sale_path;
  if (v === "cash" || v === "rto" || v === "finance") return v;
  return null;
}

export function allowedDepositUsdForDeal(args: {
  dealMetadata: unknown;
  uniqueOrgDeposits: readonly number[];
  depositsForSalePath: readonly { path: "cash" | "rto" | "finance"; amount: number }[];
}): number | null {
  const path = salePathFromDealMetadata(args.dealMetadata);
  if (path) {
    const unique = [
      ...new Set(args.depositsForSalePath.filter((d) => d.path === path).map((d) => d.amount)),
    ];
    if (unique.length === 1) return unique[0]!;
  }
  if (args.uniqueOrgDeposits.length === 1) return args.uniqueOrgDeposits[0]!;
  return null;
}

export type DealPayableOverageHint = {
  deal_id: string;
  display_name: string;
  collected_usd: number;
  allowed_deposit_usd: number;
  amount_owed_usd: number;
};

/** Deals that collected more than the org-authored dealer deposit and are not yet on the AP scroll. */
export function dealManufacturerPayablesOverageHints(args: {
  deals: readonly { id: string; display_name: string | null; metadata?: unknown }[];
  linkedDealIds: ReadonlySet<string>;
  uniqueOrgDeposits: readonly number[];
  depositsForSalePath: readonly { path: "cash" | "rto" | "finance"; amount: number }[];
}): DealPayableOverageHint[] {
  const out: DealPayableOverageHint[] = [];
  for (const deal of args.deals) {
    if (args.linkedDealIds.has(deal.id)) continue;
    const collected = payablesCollectedUsdFromDealMetadata(deal.metadata);
    if (collected == null) continue;
    const allowed = allowedDepositUsdForDeal({
      dealMetadata: deal.metadata,
      uniqueOrgDeposits: args.uniqueOrgDeposits,
      depositsForSalePath: args.depositsForSalePath,
    });
    if (allowed == null) continue;
    const amount_owed_usd = manufacturerOverageUsd(collected, allowed);
    if (amount_owed_usd <= 0) continue;
    out.push({
      deal_id: deal.id,
      display_name: (deal.display_name ?? "").trim() || deal.id.slice(0, 8),
      collected_usd: collected,
      allowed_deposit_usd: allowed,
      amount_owed_usd,
    });
  }
  return out;
}

export function isManufacturerPayableCategory(v: string): v is ManufacturerPayableCategory {
  return (MANUFACTURER_PAYABLE_CATEGORIES as readonly string[]).includes(v);
}

export function isManufacturerPayableStatus(v: string): v is ManufacturerPayableStatus {
  return (MANUFACTURER_PAYABLE_STATUSES as readonly string[]).includes(v);
}
